import { Check, Clock, Lock, Pencil, Search, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/Input';
import { QuestionnaireTagChips, QuestionnaireTagFilter } from '@/components/questionnaires/QuestionnaireTagFilter';
import type { QuestionnaireList } from '@/hooks/useQuestionnaireLibraryPrefs';
import type { QuestionnairePickerItem } from '@/lib/questionnairePickerLabel';
import { collectQuestionnaireTags, matchesQuestionnaireTags } from '@/lib/questionnaireTags';
import { cn } from '@/utils/cn';

export function filterPickerItems(
  items: readonly QuestionnairePickerItem[],
  query: string,
  selectedTags: readonly string[],
): QuestionnairePickerItem[] {
  const needle = query.trim().toLowerCase();
  return items.filter((item) => {
    if (!matchesQuestionnaireTags(item.tags, selectedTags)) return false;
    if (!needle) return true;
    const hay = `${item.name} ${item.detail} ${item.description ?? ''} ${item.tags.join(' ')}`.toLowerCase();
    return hay.includes(needle);
  });
}

export function QuestionnairePickerRow({
  item,
  selected,
  locked,
  favorite,
  pending = false,
  compact = false,
  onToggle,
  onToggleFavorite,
}: {
  item: QuestionnairePickerItem;
  selected: boolean;
  locked: boolean;
  favorite: boolean;
  pending?: boolean;
  compact?: boolean;
  onToggle: () => void;
  onToggleFavorite?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      className={cn('pk-row', compact && 'compact', favorite && 'fav', selected && 'on')}
      role="option"
      aria-selected={selected}
      aria-disabled={locked || undefined}
    >
      <button type="button" className="pk-row-main" disabled={locked} onClick={onToggle}>
        <span className={cn('cb', selected && 'on')} aria-hidden>
          {selected ? <Check /> : null}
        </span>
        <span className="m">
          <span className="nl">
            <span className="nmx">{item.name}</span>
            {compact ? null : <QuestionnaireTagChips tags={item.tags} />}
            {!compact && pending ? (
              <span className="pendc">
                <Clock className="i xs" aria-hidden />
                {t('forms.picker.pending', 'Afventer svar')}
              </span>
            ) : null}
            {locked ? <span className="tagx">{t('treatment_courses.picker_on_step')}</span> : null}
          </span>
          {compact ? (
            item.detail ? <p className="ds">{item.detail}</p> : null
          ) : item.description ? (
            <p className="ds">{item.description}</p>
          ) : item.detail ? (
            <p className="ds">{item.detail}</p>
          ) : null}
        </span>
        {!compact ? (
          item.questionCount ? (
            <span className="qn num">
              {t('forms.picker.questions', {
                count: item.questionCount,
                defaultValue: `${item.questionCount} spørgsmål`,
              })}
            </span>
          ) : (
            <span className="qn" />
          )
        ) : null}
      </button>
      {onToggleFavorite ? (
        <button
          type="button"
          className={cn('star', favorite && 'on')}
          aria-pressed={favorite}
          aria-label={
            favorite
              ? t('forms.picker.unfavorite', { name: item.name, defaultValue: `Fjern ${item.name} fra favoritter` })
              : t('forms.picker.mark_favorite', { name: item.name, defaultValue: `Markér ${item.name} som favorit` })
          }
          onClick={(event) => {
            event.stopPropagation();
            onToggleFavorite();
          }}
        >
          <Star className="i sm" aria-hidden />
        </button>
      ) : null}
    </li>
  );
}

export function QuestionnairePickerCatalog({
  items,
  selectedIds,
  onToggle,
  lockedIds,
  pendingIds,
  favoriteIds,
  onToggleFavorite,
  lists,
  onToggleList,
  recentIds,
  query,
  onQueryChange,
  selectedTags,
  onTagsChange,
  showSearch = true,
  showLists = true,
  showFavorites = true,
  showRecent = true,
  showAll = true,
  note,
  onEditLists,
}: {
  items: readonly QuestionnairePickerItem[];
  selectedIds: readonly string[];
  onToggle: (id: string) => void;
  lockedIds?: readonly string[];
  pendingIds?: readonly string[];
  favoriteIds?: readonly string[];
  onToggleFavorite?: (id: string) => void;
  lists?: readonly QuestionnaireList[];
  onToggleList?: (list: QuestionnaireList) => void;
  recentIds?: readonly string[];
  query: string;
  onQueryChange: (value: string) => void;
  selectedTags: readonly string[];
  onTagsChange: (tags: string[]) => void;
  showSearch?: boolean;
  showLists?: boolean;
  showFavorites?: boolean;
  showRecent?: boolean;
  showAll?: boolean;
  note?: { title: string; body: string } | null;
  onEditLists?: () => void;
}) {
  const { t } = useTranslation();
  const locked = new Set(lockedIds ?? []);
  const pending = new Set(pendingIds ?? []);
  const favorites = new Set(favoriteIds ?? []);
  const selected = new Set(selectedIds);
  const byId = new Map(items.map((item) => [item.id, item]));
  const tags = collectQuestionnaireTags(items);
  const filtered = filterPickerItems(items, query, selectedTags);

  function row(item: QuestionnairePickerItem) {
    return (
      <QuestionnairePickerRow
        key={item.id}
        item={item}
        selected={selected.has(item.id) || locked.has(item.id)}
        locked={locked.has(item.id)}
        favorite={favorites.has(item.id)}
        pending={pending.has(item.id)}
        onToggle={() => onToggle(item.id)}
        onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(item.id) : undefined}
      />
    );
  }

  const favoriteItems = filtered.filter((item) => favorites.has(item.id));
  const recentItems = (recentIds ?? [])
    .map((id) => byId.get(id))
    .filter((item): item is QuestionnairePickerItem => Boolean(item))
    .filter((item) => filtered.includes(item));
  const platform = filtered.filter((item) => item.group === 'platform');
  const clinic = filtered.filter((item) => item.group === 'clinic');
  const visibleLists = (lists ?? []).filter((list) =>
    list.templateIds.some((id) => byId.has(id)),
  );

  return (
    <div className="pk-catalog sk">
      {note ? (
        <div className="qp-note">
          <Lock className="i sm" aria-hidden />
          <div>
            {note.title}
            <span>{note.body}</span>
          </div>
        </div>
      ) : null}
      {showSearch ? (
        <div className="pk-search">
          <div className="search">
            <Input
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder={t('treatment_courses.picker_search')}
              aria-label={t('treatment_courses.picker_search')}
              leadingIcon={<Search className="i sm" aria-hidden />}
            />
          </div>
        </div>
      ) : null}
      <QuestionnaireTagFilter
        tags={tags}
        selected={selectedTags}
        onChange={onTagsChange}
        labelled={false}
        variant="dropdown"
        usageSource={items}
      />
      {showLists ? (
        <section className="pk-sec" aria-labelledby="h-l">
          <p className="h4" id="h-l">
            {t('forms.picker.my_lists', 'Mine lister')}
            <span className="count num">{visibleLists.length}</span>
            <span className="priv">
              <Lock className="i xs" aria-hidden />
              {t('forms.picker.private', 'Kun synlige for dig')}
            </span>
            {onEditLists ? (
              <button type="button" className="lnk r" onClick={onEditLists}>
                <Pencil className="i xs" aria-hidden />
                {t('forms.picker.edit_lists', 'Redigér lister')}
              </button>
            ) : null}
          </p>
          {visibleLists.length > 0 ? (
            <ul className="pk-l" role="listbox" aria-multiselectable="true">
              {visibleLists.map((list) => {
                const ids = list.templateIds.filter((id) => byId.has(id));
                const on = ids.length > 0 && ids.every((id) => selected.has(id));
                const names = ids
                  .map((id) => byId.get(id)?.name.split(' (')[0])
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li
                    key={list.id}
                    className={cn('pk-row list pk-list', on && 'on')}
                    role="option"
                    aria-selected={on}
                  >
                    <button type="button" className="pk-row-main" onClick={() => onToggleList?.(list)}>
                      <span className={cn('cb', on && 'on')} aria-hidden>
                        {on ? <Check /> : null}
                      </span>
                      <span className="m">
                        <span className="nl">
                          <span className="nmx" style={{ fontWeight: 600 }}>
                            {list.name}
                          </span>
                        </span>
                        <p className="ds">{names}</p>
                      </span>
                      <span className="qn num">
                        {t('forms.picker.list_count', { count: ids.length, defaultValue: `${ids.length} skemaer` })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="pk-hint">{t('forms.picker.no_lists', 'Ingen lister endnu.')}</p>
          )}
        </section>
      ) : null}
      {showFavorites ? (
        <section className="pk-sec" aria-labelledby="h-f">
          <p className="h4" id="h-f">
            {t('forms.picker.favorites', 'Favoritter')}
            <span className="count num">{favoriteItems.length}</span>
          </p>
          {favoriteItems.length > 0 ? (
            <ul className="pk-l" role="listbox" aria-multiselectable="true">
              {favoriteItems.map(row)}
            </ul>
          ) : (
            <p className="pk-hint">{t('forms.picker.no_favorites', 'Ingen favoritter endnu.')}</p>
          )}
        </section>
      ) : null}
      {showRecent && recentItems.length > 0 ? (
        <section className="pk-sec" aria-labelledby="h-r">
          <p className="h4" id="h-r">
            {t('forms.picker.recent', 'Senest brugt til patienten')}
          </p>
          <ul className="pk-l" role="listbox" aria-multiselectable="true">
            {recentItems.map(row)}
          </ul>
        </section>
      ) : null}
      {showAll ? (
        <section className="pk-sec" aria-labelledby="h-a">
          <p className="h4" id="h-a">
            {t('forms.picker.all', 'Alle skemaer')}
            <span className="count num">{filtered.length}</span>
          </p>
          {platform.length > 0 ? (
            <>
              <p className="pk-grp">
                {t('treatment_courses.picker_platform')} · {platform.length}
              </p>
              <ul className="pk-l" role="listbox" aria-multiselectable="true">
                {platform.map(row)}
              </ul>
            </>
          ) : null}
          {clinic.length > 0 ? (
            <>
              <p className="pk-grp">
                {t('treatment_courses.picker_clinic')} · {clinic.length}
              </p>
              <ul className="pk-l" role="listbox" aria-multiselectable="true">
                {clinic.map(row)}
              </ul>
            </>
          ) : null}
          {filtered.length === 0 ? (
            <p className="pk-hint">{t('services.questionnaires_picker_empty')}</p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
